/**
 * CSVFilesSection Component
 * Renders a single CSV files block (input=Features OR output=Output) in PDF.
 * Variant-driven so the two blocks can be placed at different positions.
 */

import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { CSVFileEntry } from '@/types/common';
import { styles } from '../styles';
import { CSVFilesTable } from './CSVFilesTable';
import {
  getInputFileParameters,
  getOutputFileParameters,
  groupFilesForPagination,
} from '../utils';

interface CSVFilesSectionProps {
  files: (CSVFileEntry & Record<string, unknown>)[];
  variant: 'input' | 'output';
}

export const CSVFilesSection: React.FC<CSVFilesSectionProps> = ({ files, variant }) => {
  const { t } = useTranslation('pdfReport');
  const relevantFiles = files.filter((file) => file.type === variant);

  if (relevantFiles.length === 0) return null;

  const headingKey = variant === 'input' ? 'csv.input' : 'csv.output';

  return (
    <View style={styles.section}>
      <Text style={styles.subtitle}>{t(headingKey)}</Text>
      {groupFilesForPagination(relevantFiles).map((fileGroup, groupIndex) => {
        const parameters =
          variant === 'input'
            ? getInputFileParameters(t)
            : getOutputFileParameters(t, fileGroup);
        return (
          <View key={`${variant}-group-${groupIndex}`}>
            <CSVFilesTable
              fileGroup={fileGroup}
              parameters={parameters}
              groupIndex={groupIndex}
              title={t(headingKey)}
              isNewPage={groupIndex > 0}
            />
          </View>
        );
      })}
    </View>
  );
};
